<template>
  <div class="floor-viewer" ref="wrapper" :style="computedStyle">
    <div v-if="loading">
      <div class="loading-screen">
        <h1>
          {{ lang === 'en' ? 'Loading...' : lang === 'sk' ? 'Načítavanie...' : 'Ładowanie...' }}
        </h1>
      </div>
    </div>
    <div v-else>
      <div v-if="floor" class="floor-viewer__inner">
        <header>
          <h3 class="floor-viewer__heading">
            <span
              v-html="
                lang === 'sk'
                  ? 'Vybrané: ' + title
                  : lang === 'pl'
                  ? 'Wybrane: ' + title
                  : 'Selected: ' + title
              "
            ></span>
          </h3>
        </header>
        <main>
          <i-explorable
            class="floor-viewer__plan-wrapper"
            :max-zoom="floors[floor].maxZoom"
            :enable-mouse-wheel-zoom="false"
            :enable-strict-boundaries="true"
            :zoom-animation-duration="75"
            @grabstart="ongrabstart"
            @grabend="ongrabend"
            @zoom="onzoom"
          >
            <!-- Legenda farieb -->
            <div class="legend">
              <div class="legend-item">
                <span class="legend-color available"></span>
                <span>
                  {{ lang === 'en' ? 'Available' : lang === 'sk' ? 'Dostupný' : 'Dostępny' }}
                </span>
              </div>
              <div class="legend-item">
                <span class="legend-color booked"></span>
                <span>
                  {{ lang === 'en' ? 'Booked' : lang === 'sk' ? 'Rezervovaný' : 'Zarezerwowany' }}
                </span>
              </div>
              <div class="legend-item">
                <span class="legend-color sold"></span>
                <span>
                  {{ lang === 'en' ? 'Sold' : lang === 'sk' ? 'Predaný' : 'Sprzedany' }}
                </span>
              </div>
            </div>
            <div class="floor-viewer__plan-inner">
              <!-- <h3 class="floor-viewer__heading">
              <span>{{ title }}</span>
            </h3>-->
              <img :src="imgsrc" class="floor-viewer__plan" ref="plan" />
              <svg
                class="floor-viewer__plan-overlay"
                :viewBox="floors[floor].viewBox"
                ref="overlay"
              >
                <template v-for="(apartment, key) in floors[floor].apartments">
                  <polygon
                    @click="apartmentClick(apartment)"
                    :class="{
                      reserved: apartment.reserved && !apartment.sold,
                      sold: apartment.sold && !apartment.reserved,
                    }"
                    :key="'apartmnet-' + floor + '-' + key"
                    :points="apartment.points"
                  />
                </template>
              </svg>
            </div>
          </i-explorable>
        </main>
        <!-- <footer>
        <mini-plan></mini-plan>
      </footer>-->
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, HTMLBox } from 'utils/misc';
import { wait, AnimationFrame } from 'utils/timing';

import { IMG_URL } from 'src/constants';
import floors from './floors';
import { checkApartmentAvailability } from './apartmentAvailability';

import IExplorable from '../IExplorable.vue';
import MiniPlan from '../MiniPlan.vue';

const SK_TITLES = {
  'vezapodorysy2NP': '2NP, Veža',
  'vezapodorysy3NP': '3NP, Veža',
  'vezapodorysy4NP': '4NP, Veža',
  'vezapodorysy5NP': '5NP, Veža',
  'vezapodorysy6NP': '6NP, Veža',
  '3NP': '3. nadzemné podlažie – hlavná hotelová budova',
  '4NP': '4. nadzemné podlažie – hlavná hotelová budova',
  '5NP': '5. nadzemné podlažie – hlavná hotelová budova',
  '6NP': '6. nadzemné podlažie – hlavná hotelová budova',
};

const EN_TITLES = {
  'vezapodorysy2NP': '2nd floor, Tower',
  'vezapodorysy3NP': '3rd floor, Tower',
  'vezapodorysy4NP': '4th floor, Tower',
  'vezapodorysy5NP': '5th floor, Tower',
  'vezapodorysy6NP': '6th floor, Tower',
  '3NP': '3rd above-ground floor – main hotel building',
  '4NP': '4th above-ground floor – main hotel building',
  '5NP': '5th above-ground floor – main hotel building',
  '6NP': '6th above-ground floor – main hotel building',
};

const PL_TITLES = {
  'vezapodorysy2NP': '2 piętro, Wieża',
  'vezapodorysy3NP': '3 piętro, Wieża',
  'vezapodorysy4NP': '4 piętro, Wieża',
  'vezapodorysy5NP': '5 piętro, Wieża',
  'vezapodorysy6NP': '6 piętro, Wieża',
  '3NP': '3. kondygnacja nadziemna – główny budynek hotelu',
  '4NP': '4. kondygnacja nadziemna – główny budynek hotelu',
  '5NP': '5. kondygnacja nadziemna – główny budynek hotelu',
  '6NP': '6. kondygnacja nadziemna – główny budynek hotelu',
};

export default {
  name: 'floor-viewer',
  components: {
    IExplorable,
    MiniPlan,
  },
  computed: {
    ...mapGetters({
      slide: ['getSlide', 'setSlide'],
      floor: ['getFloor', 'setFloor'],
      loading: ['getLoading', 'setLoading'],
      lang: ['getLang'],
    }),
    title() {
      return this.lang === 'en'
        ? EN_TITLES[this.floor]
        : this.lang === 'sk'
        ? SK_TITLES[this.floor]
        : PL_TITLES[this.floor];
    },
    imgsrc() {
      return require('../../assets/img/floors/' + this.floor + '.png');
    },
    computedStyle() {
      return { height: this.floor == null ? '1px' : null };
    },
  },
  data() {
    return {
      floors,
      window,
      grab: false,
      animationFrame: null,
      zoom: 1,
    };
  },
  mounted() {
    this.updateApartmentAvailability();
  },
  methods: {
    async updateApartmentAvailability() {
      //this.loading = true; // Nastavíme loading na true, keď začíname kontrolovať dostupnosť bytov
      await checkApartmentAvailability(this.floors);
      this.loading = false; // Nastavíme loading na false, keď je kontrola hotová
    },
    apartmentClick(apartment) {
      const urlParams = new URLSearchParams(window.location.search);
      const lang = urlParams.get('lang') || 'sk';

      let link;
      if (lang === 'sk') {
        link = apartment.pdf; // Slovenský odkaz
      } else if (lang === 'en') {
        link = apartment.en; // Anglický odkaz
      } else if (lang === 'pl') {
        link = apartment.pl; // Poľský odkaz
      } else {
        link = apartment.pdf; // Predvolený odkaz (napr. slovenský)
      }

      window.open(link, '_blank');
    },
    ongrabstart() {
      wait(0).then(() => (this.grab = true));
    },
    ongrabend({ history }) {
      history.length == 0 ? (this.grab = false) : wait(0).then(() => (this.grab = false));
    },
    onzoom({ zoom }) {
      this.zoom = zoom;
    },
  },
  created() {
    this.animationFrame = new AnimationFrame(() => {
      if (this.$refs.plan && this.$refs.overlay) {
        const plan = new HTMLBox(this.$refs.plan);
        const overlay = new HTMLBox(this.$refs.overlay);

        overlay.width = plan.width / this.zoom;
        overlay.height = plan.height / this.zoom;
      }
    }).start();
  },
  beforeDestroy() {
    if (this.animationFrame) {
      this.animationFrame.stop();
    }
  },
};
</script>
